import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import lodash from 'lodash';

import { pluralize } from 'utils/utils';
import { getTypeFromUrl, getVideoDuration } from 'utils/media';
import { CampaignTypes, FileType } from 'constants/constants';

import { DefaultSettings } from 'store/models/Scene';
import { isAdmin } from 'store/models/User';

import TextInput from 'components/forms/TextInput';
import SelectInput from 'components/forms/SelectInput';
import Button, { ActionButton, DeleteIconButton, MoveIconButton } from 'components/Button';
import { SceneItem } from 'components/SceneItem';
import { SmallButton, TextButton } from 'components/Button';
import Tooltip from 'components/Tooltip';

// ======================
// HELPERS
// ====================== 

const REQUIRES_OPACITY = ['black', 'white'];

const format = (name, val) => {
  switch(name) {
    case 'length':
    case 'pause':
      val = (val || 0) / 1000;
      break;
    case 'slideBreak':
      val = (val || 0) / 1000;
      break;
    case 'backgroundOpacity':
      val = _.toString(val);
  }
  return val ?? '';
}

const transform = (name, val) => {
  switch(name) {
    case 'length':
    case 'pause':
      return val * 1000;
    case 'slideBreak':
      return val * 1000;
    case 'background':
      return _.isEmpty(val)
        ? null
        : val;
    case 'backgroundOpacity':
      return val
        |> _.toNumber
        |> Math.max(#, 0)
        |> Math.min(#, 100);
    default:
      return val;
  }
}





// ======================
// COMPONENT
// ======================
export const SceneSettings = ({user, scene, campaign, sceneSettings, baseSettings, onSettingsChange, onSave, onDelete, onMoveScene,...props}) => {
  const [duration, setDuration] = useState(null);
  const [durationLoaded, setDurationLoaded] = useState(false);
  const [isMovie, setIsMovie] = useState(false);
  
  useEffect(() => {
    (async function() {
      if (scene.url) {
        try {
          const type = getTypeFromUrl(scene.url);
          const isMovie = type === FileType.VIDEO;
          const duration = await getVideoDuration(scene.url);
          setDuration(Math.round(duration) * 1000);
          setIsMovie(isMovie);
          setDurationLoaded(true);
        } catch (err) {
          console.error('Error getting video duration:', err);
        }
      }
    })();
  }, [scene]);

  const getSceneInputProps = (name, label) => {
    let val = _.isUndefined(sceneSettings?.[name]) ? baseSettings?.[name] : sceneSettings?.[name];
    let isCustom = baseSettings 
      ? !_.isUndefined(sceneSettings?.[name]) && !_.isEqual(sceneSettings?.[name], baseSettings?.[name])
      : false;
    return {
      name,
      label: isCustom 
        ? <><span>{label}<span className="custom">*</span></span></> 
        : label,
      className: cx({ custom: isCustom }),
      labelExtra: isCustom && <span className="reset" onClick={() => onRemoveSetting(name)}>reset</span>,
      value: format(name, val),
      onChange,
    }
  }

  const onSetMovieLength = async () => {
    onSettingsChange?.(scene._id, { length: duration });
  }

  const onRemoveSetting = name => {
    delete sceneSettings[name]; //reset to base settings
    onSettingsChange?.(scene._id, { ...sceneSettings });
  }

  const onChange = (value, name, e, sceneId) => {
    value = transform(name, value);
    let settings = {};
    for(const k of _.keys(sceneSettings)) {
      if(sceneSettings[k] !== DefaultSettings[k])
        _.set(settings, k, sceneSettings[k]);    
    }
    _.set(settings, name, value);
    onSettingsChange?.(scene._id, settings);
  }

  const onSaveClick = () => onSave?.(scene?._id, sceneSettings);
  const onDeleteClick = () => onDelete?.(scene?._id);
  const onMoveClick = () => onMoveScene?.(scene?._id);

  let campaignType;
  let campaignLabel;
  switch(campaign?.type) {
    case CampaignTypes.ALL:
      campaignType = 'All Channels';
      campaignLabel = 'This campaign will show in All Channels';
      break;
    case CampaignTypes.CHANNELS:
      let cl = pluralize('Channel', _.size(campaign.channels));  
      campaignType = `${_.size(campaign.channels)} ${cl}`;
      campaignLabel = `This campaign will show in ${_.size(campaign.channels)} ${cl}`;
      break;
    case CampaignTypes.TAGS:
      let tl = pluralize('Tag', _.size(campaign.tags));
      campaignType = `${_.size(campaign.tags)} ${tl}`;
      campaignLabel = `This campaign will show in channels with ${_.size(campaign.tags)} ${tl}`;
      break;
  }
  
  return (
    <div className="g-scene-settings">
      <div className="campaign-info">
        <Tooltip tooltip={`This slide is from the campaign: "${campaign?.name}"`}>
          <div className="name">{ campaign?.name }</div>
        </Tooltip>
        <Tooltip tooltip={campaignLabel}>
          <div className="targeting">{ campaignType }</div>
        </Tooltip>
      </div>
      <div className="preview">
        <SceneItem scene={scene} settings={sceneSettings} baseSettings={baseSettings} showStyle={true} />
      </div>
      <div className="settings">
        <TextInput {...getSceneInputProps('length', 'Slide Length (sec)')} helper={isMovie &&
            <SmallButton onClick={onSetMovieLength} disabled={!durationLoaded}>Set to Movie Length</SmallButton>} />
        <TextInput {...getSceneInputProps('slideBreak', 'Pause Before Showing (sec)')} />
        <SelectInput {...getSceneInputProps('background', 'Background')}>
          <option value="">None</option>
          <option value="black">Black</option>
          <option value="white">White</option>
        </SelectInput>
        { REQUIRES_OPACITY.includes(sceneSettings?.background) &&
          <TextInput
            type="number"
            max={100}
            min={0}
            {...getSceneInputProps('backgroundOpacity', 'Opacity')} /> }
        <div className="settings-actions">
          <ActionButton onClick={onSaveClick}>Done</ActionButton>
          <div className="secondary-actions">
            { onDelete && 
            <Tooltip tooltip="Remove Scene">
              <DeleteIconButton className="trash" onClick={onDeleteClick} />
            </Tooltip>}
            { onMoveScene && 
            <Tooltip tooltip="Migrate Scene To Another Campaign">
              <MoveIconButton onClick={onMoveClick} />
            </Tooltip>}
          </div>
        </div>
      </div>
    </div>)
}
export default SceneSettings;